import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker'; // Added TimePicker
import { useTranslation } from 'react-i18next';
import { organisationService } from '../../services/serviceFactory';
import { Shift } from '../../services/interfaces';

const ShiftManagement: React.FC = () => {
  const { t } = useTranslation();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentShift, setCurrentShift] = useState<Shift | null>(null);
  const [formData, setFormData] = useState<Partial<Shift>>({
    name: '',
    description: '',
    orderCutoffTimeOffsetDays: 1,
    orderDeliveryDateTime: new Date(), // We still use Date object for the form
    isApproved: true
  });

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getShifts();
      setShifts(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
      setError(t('shifts.errorLoadingShifts'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      setFormData({
        ...formData,
        [name]: numValue
      });
    }
  };

  const handleTimeChange = (time: Date | null, context?: any) => {
    if (time) {
      // Format the Date to HH:MM string format
      const hours = time.getHours().toString().padStart(2, '0');
      const minutes = time.getMinutes().toString().padStart(2, '0');
      const timeString = `${hours}:${minutes}`;
      
      setFormData({
        ...formData,
        orderDeliveryTime: timeString
      });
    }
  };

  const handleOpenDialog = (shift: Shift | null = null) => {
    if (shift) {
      // When editing an existing shift, convert the timestring to a Date
      const timeString = shift.orderDeliveryTime;
      
      // Create a date object with just the time component
      const timeParts = typeof timeString === 'string' ? timeString.split(':') : ['00', '00'];
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      
      setCurrentShift(shift);
      setFormData({
        name: shift.name,
        description: shift.description || '',
        orderCutoffTimeOffsetDays: shift.orderCutoffTimeOffsetDays,
        orderDeliveryTime: `${hours}:${minutes}:00`, // Add seconds for DateTimePicker
        orderDeliveryDateTime: new Date(1, 1, 1, hours, minutes, 0), // Set the time part only
        isApproved: shift.isApproved
      });
    } else {
      setCurrentShift(null);
      setFormData({
        name: '',
        description: '',
        orderCutoffTimeOffsetDays: 1,
        orderDeliveryTime: '00:00:00',
        isApproved: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentShift(null);
  };

  const handleOpenDeleteDialog = (shift: Shift) => {
    setCurrentShift(shift);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCurrentShift(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      // Format the time for API submission (ensure HH:MM:SS format)
      const deliveryTime = formData.orderDeliveryTime;
      console.log('handleSubmit:',deliveryTime);
      const timeString = (() => {
        // If not a string or empty, return default
        if (typeof deliveryTime !== 'string' || !deliveryTime) {
          return '00:00:00';
        }
        
        // Check if it matches xx:xx or xx:xx:xx pattern
        if (/^\d{1,2}:\d{1,2}$/.test(deliveryTime)) {
          // xx:xx format, pad and append seconds
          const [hours, minutes] = deliveryTime.split(':');
          return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
        } else if (/^\d{1,2}:\d{1,2}:\d{2}$/.test(deliveryTime)) {
          // xx:xx:xx format, pad all components
          const [hours, minutes, seconds] = deliveryTime.split(':');
          return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds}`;
        }
        
        // Invalid format, return default
        return '00:00:00';
      })();
      
      if (currentShift) {
        // Update existing shift
        await organisationService.updateShift({
          ...currentShift,
          ...formData,
          orderDeliveryTime: timeString
        });
      } else {
        // Create new shift
        await organisationService.addShift({
          ...formData,
          orderDeliveryTime: timeString
        });
      }
      handleCloseDialog();
      await fetchShifts();
    } catch (err) {
      console.error('Failed to save shift:', err);
      setError(currentShift ? t('shifts.errorUpdatingShift') : t('shifts.errorAddingShift'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentShift) return;
    
    try {
      setLoading(true);
      await organisationService.deleteShift(currentShift.id);
      handleCloseDeleteDialog();
      await fetchShifts();
    } catch (err) {
      console.error('Failed to delete shift:', err);
      setError(t('shifts.errorDeletingShift'));
    } finally {
      setLoading(false);
    }
  };
  
  const formatTime = (timeValue: string | Date) => {
    if (typeof timeValue === 'string') {
      // If already a string, check if it looks like HH:MM
      if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeValue)) {
        // Just display the HH:MM format
        const parts = timeValue.split(':');
        return `${parts[0]}:${parts[1]}`;
      }
      // If it's a different format, try to parse it
      try {
        const date = new Date(timeValue);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return timeValue; // Return as is if parsing fails
      }
    } else if (timeValue instanceof Date) {
      // If it's a Date object, format it
      return timeValue.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return 'Invalid time';
  };

  if (loading && shifts.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('organisation.shiftManagement')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('shifts.addShift')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('shifts.name')}</TableCell>
              <TableCell>{t('shifts.description')}</TableCell>
              <TableCell>{t('shifts.cutoffTimeOffset')}</TableCell>
              <TableCell>{t('shifts.deliveryTime')}</TableCell>
              <TableCell>{t('shifts.status')}</TableCell>
              <TableCell>{t('shifts.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map((shift) => (
              <TableRow key={shift.id}>
                <TableCell>{shift.name}</TableCell>
                <TableCell>{shift.description || '-'}</TableCell>
                <TableCell>{t('shifts.daysBefore', { count: shift.orderCutoffTimeOffsetDays })}</TableCell>
                <TableCell>{formatTime(shift.orderDeliveryTime)}</TableCell>
                <TableCell>
                  {shift.isApproved ? (
                    <Alert severity="success" icon={false} sx={{ py: 0 }}>
                      {t('shifts.active')}
                    </Alert>
                  ) : (
                    <Alert severity="error" icon={false} sx={{ py: 0 }}>
                      {t('shifts.inactive')}
                    </Alert>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    <Tooltip title={t('shifts.edit')}>
                      <IconButton onClick={() => handleOpenDialog(shift)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('shifts.delete')}>
                      <IconButton 
                        onClick={() => handleOpenDeleteDialog(shift)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {shifts.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('shifts.noShifts')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Shift Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentShift ? t('shifts.editShift') : t('shifts.addShift')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t('shifts.name')}
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label={t('shifts.description')}
                value={formData.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="orderCutoffTimeOffsetDays"
                label={t('shifts.cutoffTimeOffset')}
                type="number"
                value={formData.orderCutoffTimeOffsetDays}
                onChange={handleNumberChange}
                fullWidth
                required
                InputProps={{
                  endAdornment: <Typography variant="body2">{t('shifts.days')}</Typography>,
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* Replace DateTimePicker with TimePicker */}
              <TimePicker
                label={t('shifts.deliveryTime')}
                value={formData.orderDeliveryDateTime}
                onChange={handleTimeChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="isApproved"
                    checked={formData.isApproved}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label={t('shifts.active')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.name}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>{t('shifts.deleteShiftTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('shifts.deleteShiftConfirm', { name: currentShift?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftManagement;